import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiTarget, FiCheckCircle, FiArrowRight, FiX, FiCalendar, FiTrendingUp, FiFlag, FiStar, FiPlus } from 'react-icons/fi';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Label from '../ui/Label';
import Textarea from '../ui/Textarea';

const OverallGoalOnboarding = ({ isOpen, onClose, onComplete, onCreateGoal }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [goalData, setGoalData] = useState({
    title: '',
    description: '',
    category: 'Technical',
    targetDate: '',
    milestones: []
  });
  const [newMilestone, setNewMilestone] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const categories = ['Technical', 'Fitness', 'Social', 'Competition', 'Mental'];

  const steps = [
    {
      id: 1,
      icon: FiTarget,
      iconColor: 'text-blue-500',
      iconBg: 'bg-blue-100',
      title: 'Welcome to Overall Goals! 🎯',
      description: 'Overall Goals are your long-term aspirations in No-Gi Jiu-Jitsu. These help you stay focused and track meaningful progress over weeks or months.',
      highlight: 'Set ambitious targets like mastering a position, improving fitness, or preparing for competition.',
      image: '🎯',
      type: 'info'
    },
    {
      id: 2,
      icon: FiFlag,
      iconColor: 'text-purple-500',
      iconBg: 'bg-purple-100',
      title: 'What\'s Your Goal? 📝',
      description: 'Give your goal a clear, specific title and describe what you want to achieve.',
      highlight: 'Example: "Master Leg Locks" or "Compete in Blue Belt Division"',
      image: '📝',
      type: 'form-basic'
    },
    {
      id: 3,
      icon: FiCalendar,
      iconColor: 'text-green-500',
      iconBg: 'bg-green-100',
      title: 'Break It Down with Milestones 📋',
      description: 'Every big goal needs smaller steps. Add milestones to track incremental progress and celebrate wins along the way.',
      highlight: 'Example: Learn heel hook defense → Drill ashi garami entries → Hit 5 successful submissions',
      image: '📋',
      type: 'form-milestones'
    },
    {
      id: 4,
      icon: FiTrendingUp,
      iconColor: 'text-yellow-500',
      iconBg: 'bg-yellow-100',
      title: 'Track Your Progress 📈',
      description: 'As you complete milestones, your progress percentage automatically updates. Watch your journey unfold with visual progress bars!',
      highlight: 'Completed milestones give you momentum and show how far you\'ve come.',
      image: '📈',
      type: 'info'
    },
    {
      id: 5,
      icon: FiStar,
      iconColor: 'text-orange-500',
      iconBg: 'bg-orange-100',
      title: 'You\'re All Set! 🚀',
      description: 'Your first Overall Goal is ready! Keep adding milestones, track your progress, and crush your training objectives.',
      highlight: 'Remember: Consistency beats intensity. Small steps lead to big achievements!',
      image: '🚀',
      type: 'complete'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      // Validate current step before moving forward
      if (steps[currentStep].type === 'form-basic') {
        if (!goalData.title.trim()) {
          alert('Please enter a goal title to continue');
          return;
        }
      }
      if (steps[currentStep].type === 'form-milestones') {
        if (goalData.milestones.length === 0) {
          if (!window.confirm('You haven\'t added any milestones yet. Continue anyway?')) {
            return;
          }
        }
      }
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('clinchfirst_overall_goal_onboarding_completed', 'true');
    
    // Pass the goal data back to parent
    if (onCreateGoal) {
      onCreateGoal(goalData);
    }
    
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip the onboarding? You can still create goals later.')) {
      localStorage.setItem('clinchfirst_overall_goal_onboarding_completed', 'true');
      onClose();
    }
  };

  const addMilestone = () => {
    if (newMilestone.trim()) {
      setGoalData({
        ...goalData,
        milestones: [
          ...goalData.milestones,
          { id: Date.now(), text: newMilestone, completed: false }
        ]
      });
      setNewMilestone('');
    }
  };

  const removeMilestone = (id) => {
    setGoalData({
      ...goalData,
      milestones: goalData.milestones.filter((m) => m.id !== id)
    });
  };

  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleSkip}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full pointer-events-auto overflow-hidden max-h-[90vh] flex flex-col"
            >
              {/* Header with Close Button */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={handleSkip}
                  className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700"
                >
                  <FiX className="w-5 h-5" />
                </button>

                {/* Progress Bar */}
                <div className="h-2 bg-slate-100">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="p-8 sm:p-12 overflow-y-auto flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Icon */}
                    <div className="flex justify-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                        className={`w-20 h-20 rounded-full ${currentStepData.iconBg} flex items-center justify-center`}
                      >
                        <StepIcon className={`w-10 h-10 ${currentStepData.iconColor}`} />
                      </motion.div>
                    </div>

                    {/* Title */}
                    <div className="text-center">
                      <motion.h2
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3"
                      >
                        {currentStepData.title}
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-base sm:text-lg text-slate-600 leading-relaxed"
                      >
                        {currentStepData.description}
                      </motion.p>
                    </div>

                    {/* Highlight Box */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                            <FiCheckCircle className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                          {currentStepData.highlight}
                        </p>
                      </div>
                    </motion.div>

                    {/* Form Content Based on Step Type */}
                    {currentStepData.type === 'form-basic' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="space-y-4 bg-white border-2 border-slate-200 rounded-xl p-6"
                      >
                        <div>
                          <Label htmlFor="goal-title">Goal Title *</Label>
                          <Input
                            id="goal-title"
                            placeholder="e.g., Master Leg Locks, Get in Shape"
                            value={goalData.title}
                            onChange={(e) =>
                              setGoalData({ ...goalData, title: e.target.value })
                            }
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="goal-description">Description</Label>
                          <Textarea
                            id="goal-description"
                            placeholder="Describe your goal in detail..."
                            value={goalData.description}
                            onChange={(e) =>
                              setGoalData({ ...goalData, description: e.target.value })
                            }
                            rows={3}
                            className="mt-1"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="goal-category">Category</Label>
                            <select
                              id="goal-category"
                              value={goalData.category}
                              onChange={(e) =>
                                setGoalData({ ...goalData, category: e.target.value })
                              }
                              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-1"
                            >
                              {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <Label htmlFor="goal-target-date">Target Date (Optional)</Label>
                            <Input
                              id="goal-target-date"
                              type="date"
                              value={goalData.targetDate}
                              onChange={(e) =>
                                setGoalData({ ...goalData, targetDate: e.target.value })
                              }
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {currentStepData.type === 'form-milestones' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="space-y-4 bg-white border-2 border-slate-200 rounded-xl p-6"
                      >
                        <div>
                          <Label>Add Milestones</Label>
                          <p className="text-xs text-slate-600 mb-2">
                            Break your goal into smaller, actionable steps
                          </p>
                          <div className="flex gap-2 mb-3">
                            <Input
                              placeholder="Add a milestone..."
                              value={newMilestone}
                              onChange={(e) => setNewMilestone(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addMilestone();
                                }
                              }}
                            />
                            <Button type="button" onClick={addMilestone}>
                              <FiPlus className="w-4 h-4" />
                            </Button>
                          </div>

                          {goalData.milestones.length > 0 ? (
                            <div className="space-y-2">
                              {goalData.milestones.map((milestone) => (
                                <div
                                  key={milestone.id}
                                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                                >
                                  <div className="flex items-center space-x-2 flex-1">
                                    <FiCheckCircle className="w-4 h-4 text-green-600" />
                                    <span className="text-sm text-slate-900">{milestone.text}</span>
                                  </div>
                                  <button
                                    onClick={() => removeMilestone(milestone.id)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <FiX className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                              <FiTarget className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <p className="text-sm text-slate-600">No milestones added yet</p>
                              <p className="text-xs text-slate-500 mt-1">
                                Add at least one milestone to track progress
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* Large Emoji Illustration - Only for info steps */}
                    {(currentStepData.type === 'info' || currentStepData.type === 'complete') && (
                      <motion.div
                        initial={{ scale: 0, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.6, type: 'spring', stiffness: 150 }}
                        className="flex justify-center py-4"
                      >
                        <span className="text-7xl sm:text-8xl">{currentStepData.image}</span>
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-8 py-6 flex items-center justify-between border-t border-slate-200 flex-shrink-0">
                {/* Step Indicators */}
                <div className="flex items-center space-x-2">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentStep
                          ? 'bg-blue-600 w-8'
                          : index < currentStep
                          ? 'bg-blue-400'
                          : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center space-x-3">
                  {currentStep > 0 && (
                    <Button
                      variant="ghost"
                      onClick={handlePrevious}
                      className="text-slate-600"
                    >
                      Previous
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6"
                  >
                    {currentStep === steps.length - 1 ? (
                      <>
                        Create Goal
                        <FiCheckCircle className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Next
                        <FiArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OverallGoalOnboarding;